import { useEffect, useId } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import type { Resolver } from 'react-hook-form'
import { Modal, Button, Input, Select, TimeInput } from '@/components/ui'
import { SectionLoader } from '@/components/common/SectionLoader'
import type { SelectOption } from '@/components/ui'
import { useShift, useUpdateShift } from '../hooks/useShifts'
import { editShiftFormSchema, type EditShiftFormData } from '../schemas/shift.schemas'

export interface EditShiftModalProps {
  companyId: string
  shiftId: string | null
  open: boolean
  onClose: () => void
}

const statusOptions: SelectOption[] = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
]

export function EditShiftModal({
  companyId,
  shiftId,
  open,
  onClose,
}: EditShiftModalProps): React.ReactElement {
  const formId = useId()
  const { data: shift, isLoading } = useShift(companyId, shiftId)
  const updateShiftMutation = useUpdateShift()
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    control,
    formState: { errors },
  } = useForm<EditShiftFormData>({
    resolver: zodResolver(editShiftFormSchema) as Resolver<EditShiftFormData>,
    defaultValues: {
      name: '',
      code: '',
      startTime: '09:00',
      endTime: '17:00',
      gracePeriodMinutes: 0,
      maxEarlyClockInMinutes: 10,
      allowClockInPastEnd: false,
      lateClockInCutoffMinutes: 0,
      color: '',
      description: '',
      status: 'active',
    },
  })

  useEffect(() => {
    if (shift) {
      reset({
        name: shift.name,
        code: shift.code ?? '',
        startTime: shift.startTime,
        endTime: shift.endTime,
        gracePeriodMinutes: shift.gracePeriodMinutes ?? 0,
        maxEarlyClockInMinutes: shift.maxEarlyClockInMinutes ?? 10,
        allowClockInPastEnd: shift.allowClockInPastEnd ?? false,
        lateClockInCutoffMinutes: shift.lateClockInCutoffMinutes ?? 0,
        color: shift.color ?? '',
        description: shift.description ?? '',
        status: shift.status ?? 'active',
      })
    }
  }, [shift, reset])

  const onSubmit = (data: EditShiftFormData) => {
    if (!shiftId) return
    updateShiftMutation.mutate(
      {
        companyId,
        id: shiftId,
        payload: {
          name: data.name,
          code: data.code || undefined,
          startTime: data.startTime,
          endTime: data.endTime,
          gracePeriodMinutes: data.gracePeriodMinutes ?? 0,
          maxEarlyClockInMinutes: data.maxEarlyClockInMinutes ?? 10,
          allowClockInPastEnd: data.allowClockInPastEnd ?? false,
          lateClockInCutoffMinutes: data.lateClockInCutoffMinutes ?? 0,
          color: data.color || undefined,
          description: data.description || undefined,
          status: (data.status ?? 'active') as 'active' | 'inactive',
        },
      },
      {
        onSuccess: () => {
          reset()
          onClose()
        },
      }
    )
  }

  if (!open) return <></>

  const showFormFooter = Boolean(!isLoading && shift)

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit shift"
      className="max-w-2xl"
      footer={
        showFormFooter ? (
          <>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              form={formId}
              type="submit"
              variant="primary"
              loading={updateShiftMutation.isPending}
            >
              Save
            </Button>
          </>
        ) : null
      }
    >
      {isLoading && (
        <SectionLoader variant="detail" label="Loading shift" />
      )}
      {!isLoading && shift && (
        <form id={formId} onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          {shift.isTemplate && (
            <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-200">
              This is a template. Saving updates all future, still-scheduled
              shifts assigned from it — except shifts that were manually edited.
            </p>
          )}
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <Input
              label="Name"
              {...register('name')}
              error={errors.name?.message}
              placeholder="e.g. Morning Shift"
            />
            <Input
              label="Code (optional)"
              {...register('code')}
              error={errors.code?.message}
              placeholder="e.g. MS-1"
            />
          </div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <Controller
              control={control}
              name="startTime"
              render={({ field }) => (
                <TimeInput
                  label="Start time"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  name={field.name}
                  error={errors.startTime?.message}
                />
              )}
            />
            <Controller
              control={control}
              name="endTime"
              render={({ field }) => (
                <TimeInput
                  label="End time"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  name={field.name}
                  error={errors.endTime?.message}
                />
              )}
            />
          </div>
          <EditShiftDurationHint
            startTime={watch('startTime')}
            endTime={watch('endTime')}
          />
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <Input
              label="Grace period (minutes)"
              type="number"
              min={0}
              {...register('gracePeriodMinutes', { valueAsNumber: true })}
              error={errors.gracePeriodMinutes?.message}
            />
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Status
              </label>
              <Select
                options={statusOptions}
                value={watch('status') ?? 'active'}
                onChange={(v) => setValue('status', v as 'active' | 'inactive', { shouldValidate: true })}
              />
            </div>
          </div>
          <fieldset className="space-y-3 rounded-xl border border-gray-200 p-3 dark:border-gray-700">
            <legend className="px-1 text-sm font-semibold text-gray-700 dark:text-gray-300">
              Clock-in window
            </legend>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Beyond these limits, staff must request a time correction.
            </p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Input
                label="Allow early clock-in (minutes before start)"
                type="number"
                min={0}
                {...register('maxEarlyClockInMinutes', { valueAsNumber: true })}
                error={errors.maxEarlyClockInMinutes?.message}
              />
              <Input
                label="Automatic clock-out cutoff (minutes after end)"
                type="number"
                min={0}
                {...register('lateClockInCutoffMinutes', { valueAsNumber: true })}
                error={errors.lateClockInCutoffMinutes?.message}
                disabled={watch('allowClockInPastEnd') === true}
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                {...register('allowClockInPastEnd')}
              />
              Allow clock-in after shift ends
            </label>
          </fieldset>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <Input
              label="Color (optional)"
              {...register('color')}
              placeholder="#4CAF50"
            />
            <Input
              label="Description (optional)"
              {...register('description')}
              placeholder="e.g. Morning shift for software engineers"
            />
          </div>
        </form>
      )}
    </Modal>
  )
}

EditShiftModal.displayName = 'EditShiftModal'

const HH_MM_RE = /^([01]\d|2[0-3]):([0-5]\d)$/

function EditShiftDurationHint({
  startTime,
  endTime,
}: {
  startTime?: string
  endTime?: string
}): React.ReactElement | null {
  if (!startTime || !endTime) return null
  if (!HH_MM_RE.test(startTime) || !HH_MM_RE.test(endTime)) return null
  const [sh, sm] = startTime.split(':').map(Number)
  const [eh, em] = endTime.split(':').map(Number)
  let startMin = sh * 60 + sm
  let endMin = eh * 60 + em
  const crossesMidnight = endMin <= startMin
  if (crossesMidnight) endMin += 1440
  const duration = endMin - startMin
  if (duration === 0) return null
  const h = Math.floor(duration / 60)
  const m = duration % 60
  const durationText = m === 0 ? `${h}h` : `${h}h ${m}m`
  return (
    <p className="-mt-2 text-xs text-gray-500 dark:text-gray-400">
      Duration: <span className="font-medium text-gray-700 dark:text-gray-300">{durationText}</span>
      {crossesMidnight && (
        <span className="ml-2 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-200">
          Ends next day
        </span>
      )}
    </p>
  )
}
